import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {Switch} from 'shells/switch/switch';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {ChatIconEnum, ChatbotIconEnum} from 'nutshell-graphql-types';
import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {useDebouncedSaveField} from '../actions-options/use-debounced-save-field';
import {WidgetColorPicker} from '../config-options/widget-color-picker';
import {WidgetIconPicker} from '../config-options/widget-icon-picker';
import {SAVE_STATE_KEYS, SAVE_DELAY} from '../consts';
import type {SaveStateKey} from '../consts';

import {ChatbotIconPicker} from './chatbot-icon-picker';

type Props = {
    isLoading: boolean;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
    color: string;
    secondaryColor: string;
    iconType: ChatIconEnum;
    iconUrl?: string | null | undefined;
    name: string;
    chatbotName: string;
    chatbotIconEnum: ChatbotIconEnum;
    showPoweredBy: boolean;
    hasEngagementPro?: boolean | null | undefined;
    handleSave: (updates: ChatSettingsLookAndFeelInput, saveStateKey: SaveStateKey) => Promise<any>;
    setColor: (color: string) => void;
    setSecondaryColor: (secondaryColor: string) => void;
    setIconType: (iconType: ChatIconEnum) => void;
    setIconUrl: (iconUrl: string) => void;
    handleIconSave: (iconType: ChatIconEnum, iconUrl?: string | null | undefined) => void;
    setChatbotIconEnum: (chatbotIconEnum: ChatbotIconEnum) => void;
    setShowPoweredBy: (showPoweredBy: boolean) => void;
    setIsShowPoweredByUpsellModalOpen: (isShowPoweredByUpsellModalOpen: boolean) => void;
};

export const NAME_DISPLAY_LIMIT = 25;

export function BrandingSection(props: Props) {
    const {
        value: name,
        onChange: setName,
        onBlur: onNameBlur,
    } = useDebouncedSaveField({
        savedValue: props.name,
        handleSave: (newName) => props.handleSave({nameDisplay: newName}, SAVE_STATE_KEYS.NAME),
        validation: {maxLength: NAME_DISPLAY_LIMIT},
    });

    const {
        value: chatbotName,
        onChange: setChatbotName,
        onBlur: onChatbotNameBlur,
    } = useDebouncedSaveField({
        savedValue: props.chatbotName,
        handleSave: (newChatbotName) =>
            props.handleSave({chatbotName: newChatbotName}, SAVE_STATE_KEYS.CHATBOT_NAME),
        validation: {maxLength: NAME_DISPLAY_LIMIT},
    });

    const handleShowPoweredByClick = () => {
        if (!props.hasEngagementPro) {
            props.setIsShowPoweredByUpsellModalOpen(true);
        } else {
            props.setShowPoweredBy(!props.showPoweredBy);
            props.handleSave(
                {showPoweredBy: !props.showPoweredBy},
                SAVE_STATE_KEYS.SHOW_POWERED_BY
            );
        }
    };

    return (
        <Stack spacing={16}>
            <Body size='large' isBold={true}>
                Branding
            </Body>
            <Body>Brand your engagement bar by customizing colors, avatars and more.</Body>
            <LabelWithContent
                label='Company name'
                state={props.savingToastKey === SAVE_STATE_KEYS.NAME ? props.toastState : 'initial'}
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText isFullWidth={true} height={30} />
                    ) : (
                        <CharacterLimitedTextfield
                            value={name}
                            onChange={setName}
                            onBlur={onNameBlur}
                            maxLength={NAME_DISPLAY_LIMIT}
                            placeholder='Taylor properties'
                            isDebouncedSave={true}
                            debouncedSaveDelay={SAVE_DELAY}
                        />
                    )
                }
            />
            <LabelWithContent
                label='Chatbot name'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.CHATBOT_NAME
                        ? props.toastState
                        : 'initial'
                }
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText width={250} height={30} />
                    ) : (
                        <CharacterLimitedTextfield
                            value={chatbotName}
                            onChange={setChatbotName}
                            onBlur={onChatbotNameBlur}
                            maxLength={NAME_DISPLAY_LIMIT}
                            placeholder='Chatbot'
                            isDebouncedSave={true}
                            debouncedSaveDelay={SAVE_DELAY}
                        />
                    )
                }
            />
            {!NutClientConfig.isWebfx &&
                (props.isLoading ? (
                    <LoadingText width={250} height={30} />
                ) : (
                    <LabelWithContent
                        label='Show ‘powered by Nutshell’ watermark'
                        state={
                            props.savingToastKey === SAVE_STATE_KEYS.SHOW_POWERED_BY
                                ? props.toastState
                                : 'initial'
                        }
                        spacing={8}
                        content={
                            <Stack isHorizontal={true} spacing={8}>
                                <Switch
                                    onChange={handleShowPoweredByClick}
                                    isRounded={true}
                                    isOn={props.showPoweredBy}
                                    isSmall={true}
                                />
                                <Body> {props.showPoweredBy ? 'On' : 'Off'}</Body>
                            </Stack>
                        }
                    />
                ))}
            <LabelWithContent
                label='Logo / Avatar'
                labelPopoverProps={{
                    info: 'This will display inside the widget and header',
                    bodyColor: 'dark',
                }}
                state={props.savingToastKey === SAVE_STATE_KEYS.ICON ? props.toastState : 'initial'}
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText width={250} height={30} />
                    ) : (
                        <WidgetIconPicker
                            type={props.iconType}
                            url={props.iconUrl}
                            handleUpdate={(iconType: ChatIconEnum, iconUrl: string) => {
                                props.setIconUrl(iconUrl);
                                props.setIconType(iconType);
                                props.handleIconSave(iconType, iconUrl);
                            }}
                        />
                    )
                }
            />
            <LabelWithContent
                label='Chatbot icon'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.CHATBOT_ICON
                        ? props.toastState
                        : 'initial'
                }
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText width={250} height={30} />
                    ) : (
                        <ChatbotIconPicker
                            type={props.chatbotIconEnum}
                            setType={(newType) => {
                                props.setChatbotIconEnum(newType);
                                props.handleSave(
                                    {chatbotIconEnum: newType},
                                    SAVE_STATE_KEYS.CHATBOT_ICON
                                );
                            }}
                        />
                    )
                }
            />
            <LabelWithContent
                label='Primary color'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.COLOR ? props.toastState : 'initial'
                }
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText width={250} height={30} />
                    ) : (
                        <WidgetColorPicker
                            currentColor={props.color}
                            setCurrentColor={(newColor) => {
                                props.setColor(newColor);
                                props.handleSave({color: newColor}, SAVE_STATE_KEYS.COLOR);
                            }}
                        />
                    )
                }
            />
            <LabelWithContent
                label='Secondary color'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.SECONDARY_COLOR
                        ? props.toastState
                        : 'initial'
                }
                spacing={8}
                content={
                    props.isLoading ? (
                        <LoadingText width={250} height={30} />
                    ) : (
                        <WidgetColorPicker
                            currentColor={props.secondaryColor}
                            setCurrentColor={(newColor) => {
                                props.setSecondaryColor(newColor);
                                props.handleSave(
                                    {secondaryColor: newColor},
                                    SAVE_STATE_KEYS.SECONDARY_COLOR
                                );
                            }}
                        />
                    )
                }
            />
        </Stack>
    );
}
